import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

export function PageHeader({ kicker, title, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 portal-rise">
      <div>
        {kicker && <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">{kicker}</p>}
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#F2F2F2]">{title}</h1>
      </div>
      {children && <div className="flex flex-wrap gap-3">{children}</div>}
    </div>
  );
}

export function Card({ className = '', children, ...rest }) {
  return (
    <div className={`portal-card bg-[#0B0D10] border border-[#1F242C] rounded-sm ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-heading text-sm uppercase tracking-[0.18em] text-[#BFBFBF]">{children}</h3>
      {right}
    </div>
  );
}

export function Stat({ label, value, sub, testid }) {
  return (
    <Card className="p-6 portal-accent portal-card-hover">
      <p className="text-xs uppercase tracking-[0.2em] text-[#7A7F87] mb-3">{label}</p>
      <p className="font-mono text-2xl lg:text-3xl text-[#F2F2F2]" data-testid={testid}>{value}</p>
      {sub && <p className="text-xs text-[#7A7F87] mt-2">{sub}</p>}
    </Card>
  );
}

const BAND = { A: '#2E9E6A', B: '#7DB24A', C: '#D4AF37', D: '#D9822B', E: '#C2423A' };

export function RatingBadge({ value, size = 'md' }) {
  if (!value) return <span className="text-[#7A7F87] text-xs">n.d.</span>;
  const color = BAND[String(value).toUpperCase()] || '#7A7F87';
  const dim = size === 'lg' ? 'w-16 h-16 text-3xl' : 'w-8 h-8 text-sm';
  return (
    <span className={`inline-flex items-center justify-center font-heading font-bold rounded-sm ${dim}`}
      style={{ background: `${color}22`, color, border: `1px solid ${color}88` }}>
      {value}
    </span>
  );
}

export function Empty({ icon: Icon, title, text, children }) {
  return (
    <div className="py-16 text-center">
      {Icon && <Icon className="mx-auto text-[#3A3F47] mb-4" size={36} />}
      <p className="text-[#E5E5E5] font-medium mb-1">{title}</p>
      {text && <p className="text-sm text-[#7A7F87] max-w-md mx-auto">{text}</p>}
      {children && <div className="mt-6">{children}</div>}
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex items-center gap-3 text-[#A3A3A3] py-10 justify-center">
      <Loader2 className="animate-spin text-[#D4AF37]" size={20} /> {label || 'Caricamento…'}
    </div>
  );
}

export function Flag({ on, label }) {
  return (
    <span className={`inline-flex items-center gap-2 text-xs px-2 py-1 border rounded-sm ${on ? 'border-[#C2423A]/60 text-[#E37A72] bg-[#C2423A]/10' : 'border-[#1F242C] text-[#7A7F87]'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-[#E37A72]' : 'bg-[#2E9E6A]'}`} />
      {label}: {on ? 'Sì' : 'No'}
    </span>
  );
}

export const inputCls = 'bg-[#050505] border-[#1F242C] text-[#E5E5E5] h-11 rounded-sm focus-visible:ring-0 focus:border-[#D4AF37] placeholder:text-[#4A4F57]';

export function ReadMore({ children, className = '', clamp = 'max-sm:line-clamp-4', testid }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setLong(el.scrollHeight > el.clientHeight + 2);
    measure();
    document.fonts?.ready.then(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children]);
  return (
    <div className={className}>
      <div ref={ref} className={open ? '' : clamp} data-testid={testid}>{children}</div>
      {(long || open) && <button type="button" onClick={() => setOpen(!open)} className="sm:hidden mt-1 text-xs font-semibold text-[#D4AF37] underline underline-offset-2" data-testid={testid ? `${testid}-toggle` : undefined}>{open ? 'Mostra meno' : 'Leggi di più'}</button>}
    </div>
  );
}
export const goldBtn = 'bg-[#D4AF37] hover:bg-[#E6C547] text-[#050505] rounded-sm font-semibold transition-colors';
export const ghostBtn = 'bg-transparent border border-[#1F242C] hover:border-[#D4AF37] text-[#E5E5E5] rounded-sm transition-colors';
